import Button from '@/components/Buttons/Button';
import SentimentCheckmark from '@/images/sentiment/SentimentCheckmark.svg';
import { sentimentImageSources } from '@/pages/AdvancedEditor/utils/expressive-utils';
import theme from '@/theme';
import { AvatarSentiment, IExpressiveSentiment } from '@d-id/types';
import styled from '@emotion/styled';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding: 32px 32px 24px 32px;
    gap: 32px;
    height: 100%;
`;

const Title = styled.div`
    color: ${theme.colors.black[90]};
    font-family: Manrope;
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.48px;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    padding: 2px;
    margin: -2px;
    gap: 12px;
    flex: 1;
    overflow-y: auto;
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 16px;
`;

const StyledButton = styled(Button)`
    height: 48px;
    min-width: 96px;
`;

const SentimentCard = styled.div<{ isSelected: boolean }>`
    display: flex;
    align-items: flex-start;
    padding: 16px 12px;
    border-radius: 8px;
    border: 2px solid ${({ isSelected }) => (isSelected ? theme.colors.black.main : 'transparent')};
    background-color: ${({ isSelected }) => (isSelected ? theme.colors.black[50] : '#fff')};
    cursor: pointer;
    transition: all 0.2s ease;
    gap: 12px;
    position: relative;
    box-shadow: ${({ isSelected }) => (isSelected ? 'none' : `0 0 0 1px ${theme.colors.black[300]}`)};

    &:hover {
        border-color: ${({ isSelected }) => (isSelected ? theme.colors.black.main : 'transparent')};
        box-shadow: ${({ isSelected }) => (isSelected ? 'none' : `0 0 0 1px ${theme.colors.black.main}`)};
    }
`;

const SentimentIcon = styled.img`
    width: 32px;
    height: 32px;
    flex-shrink: 0;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const CardTitle = styled.div`
    color: ${theme.colors.black.main};
    font-family: Manrope;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
`;

const Description = styled.div`
    color: ${theme.colors.black.main};
    font-family: Manrope;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
`;

interface Props {
    value: AvatarSentiment;
    setValue: (value: AvatarSentiment) => void;
    onClose: () => void;
    onConfirm: () => void;
    availableSentiments: IExpressiveSentiment[];
}

export const SentimentSelection = ({ value, setValue, onClose, onConfirm, availableSentiments }: Props) => {
    return (
        <Wrapper>
            <Title>Choose your Avatar's sentiment</Title>
            <Container>
                {availableSentiments.map(sentiment => {
                    const isSelected = value === sentiment.sentiment;
                    return (
                        <SentimentCard key={sentiment.sentiment} isSelected={isSelected} onClick={() => setValue(sentiment.sentiment)}>
                            <SentimentIcon
                                src={isSelected ? SentimentCheckmark : sentimentImageSources[sentiment.sentiment]}
                                alt={sentiment.sentiment}
                            />
                            <ContentContainer>
                                <CardTitle>{sentiment.sentiment}</CardTitle>
                                <Description>{sentiment.description}</Description>
                            </ContentContainer>
                        </SentimentCard>
                    );
                })}
            </Container>
            <Footer>
                <StyledButton variant="outlined" color="secondary" onClick={onClose}>
                    Cancel
                </StyledButton>
                <StyledButton variant="contained" color="secondary" onClick={onConfirm}>
                    Confirm
                </StyledButton>
            </Footer>
        </Wrapper>
    );
};
